/**
 * The richrich data-viz palette — the single source of truth for chart canvas
 * colors (lightweight-charts draws to a `<canvas>`, so it cannot read the CSS
 * custom properties in `tokens.css`; these JS constants mirror those tokens
 * exactly so canvas and DOM charts read as one system).
 *
 * Values are the validated reference palette from the `dataviz` skill
 * (`references/palette.md`): OKLCH-stepped categorical hues with worst adjacent
 * CVD ΔE ≈ 24 (light) / 10 (dark), reserved status colors, and mode-selected
 * chrome. Do not eyeball-tweak these — re-run `scripts/validate_palette.js`
 * against the surfaces below if you change a hue.
 */
import type { Theme } from "./theme";

/** System UI sans, per the palette spec — no display/serif face anywhere. */
export const VIZ_FONT_FAMILY =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export interface VizPalette {
  /** Chart plotting surface (transparent-friendly base). */
  surface: string;
  /** Page plane behind the chart card. */
  page: string;
  /** Primary ink — titles, hero numbers. */
  ink: string;
  /** Secondary ink — axis labels, legends. */
  inkSecondary: string;
  /** Muted ink — de-emphasized ticks. */
  muted: string;
  /** Hairline gridlines. */
  grid: string;
  /** Baseline / axis border. */
  axis: string;
  /** Hairline ring around cards/marks. */
  border: string;
  /** Positive delta / gain (status: good). */
  good: string;
  /** Warning status. */
  warning: string;
  /** Serious status. */
  serious: string;
  /** Negative delta / loss (status: critical). */
  critical: string;
  /** "Delta up" text (higher-contrast success ink than the `good` mark). */
  upText: string;
  /** Categorical slots in fixed CVD-safe order — assign by index, never cycle. */
  categorical: readonly string[];
}

const LIGHT: VizPalette = {
  surface: "#fcfcfb",
  page: "#f9f9f7",
  ink: "#0b0b0b",
  inkSecondary: "#52514e",
  muted: "#898781",
  grid: "#e1e0d9",
  axis: "#c3c2b7",
  border: "rgba(11,11,11,0.10)",
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
  upText: "#006300",
  categorical: [
    "#2a78d6", // 1 blue
    "#1baf7a", // 2 aqua
    "#eda100", // 3 yellow
    "#008300", // 4 green
    "#4a3aa7", // 5 violet
    "#e34948", // 6 red
    "#e87ba4", // 7 magenta
    "#eb6834", // 8 orange
  ],
};

const DARK: VizPalette = {
  surface: "#1a1a19",
  page: "#0d0d0d",
  ink: "#ffffff",
  inkSecondary: "#c3c2b7",
  muted: "#898781",
  grid: "#2c2c2a",
  axis: "#383835",
  border: "rgba(255,255,255,0.10)",
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
  upText: "#0ca30c",
  categorical: [
    "#3987e5", // 1 blue
    "#199e70", // 2 aqua
    "#c98500", // 3 yellow
    "#008300", // 4 green
    "#9085e9", // 5 violet
    "#e66767", // 6 red
    "#d55181", // 7 magenta
    "#d95926", // 8 orange
  ],
};

export function paletteFor(theme: Theme): VizPalette {
  return theme === "dark" ? DARK : LIGHT;
}

/**
 * Apply an alpha channel to a `#rrggbb` hex, returning an `rgba(...)` string.
 * Used for canvas fills (area gradients) where a translucent series color is
 * needed; passes non-hex inputs through unchanged.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const int = Number.parseInt(m[1]!, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Categorical hue for slot `i` (0-based), in fixed order. Beyond the 8 defined
 * slots callers must fold into an "Other" bucket rather than generating a hue;
 * this wraps only as a defensive last resort and returns the muted ink.
 */
export function categoricalColor(palette: VizPalette, i: number): string {
  return palette.categorical[i] ?? palette.muted;
}

/**
 * Semantic P&L color: gain → good, loss → critical, flat → muted. Always pair
 * with a sign/label at the call site so meaning is never color-alone.
 */
export function pnlColor(palette: VizPalette, sign: number): string {
  if (sign > 0) return palette.good;
  if (sign < 0) return palette.critical;
  return palette.muted;
}
